import { Autocomplete } from "@mui/material";
import { Axios } from "api/axios";
import BootstrapTextField from "Common/BootstrapTextField";
import SubmitButton from "Components/Buttons/SubmitButton";
import { autocompleteDropdownStyles } from "Constants/Style";
import { City, Country, State } from "country-state-city";
import { useHandleBack } from "helpers/functions/useHandleBack";
import React, { useEffect, useState } from "react";
import { Button, Col, Form, InputGroup, Row } from "react-bootstrap";
import { BsArrowLeft, BsEnvelope, BsTelephone } from "react-icons/bs";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

const AddCustomerForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { customerData } = location.state || {};
  const { id } = useParams<{ id: string }>();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [countries, setCountries] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const [existingPANs, setExistingPANs] = useState<string[]>([]);
  const [existingGSTINs, setExistingGSTINs] = useState<string[]>([]);

  const handleBack = useHandleBack("/customer");

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    customer_name: "",
    mobile_no: "",
    email: "",
    contact_person_name: "",
    contact_person_mobile_no: "",
    pan_number: "",
    gstin: "",
    address: "",
    area: "",
    country: "",
    state: "",
    city: "",
    pincode: "",
  });

  // Load countries on mount
  useEffect(() => {
    const countryList = Country.getAllCountries();
    setCountries(countryList);
    
    // Auto-select India for new customer creation (when id is empty and country is empty)
    if (!id && !formData.country) {
      const india = countryList.find((country: any) => country.name === "India");
      if (india) {
        setFormData((prev) => {
          if (!prev.country) {
            // Load states for India
            const indiaStates = State.getStatesOfCountry(india.isoCode);
            setStates(indiaStates);
            
            return {
              ...prev,
              country: india.name,
            };
          }
          return prev;
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Fetch customer data if editing
  useEffect(() => {
    if (customerData) {
      setFormData({
        customer_name: customerData.customer_name || "",
        mobile_no: customerData.mobile_no || "",
        email: customerData.email || "",
        contact_person_name: customerData.contact_person_name || "",
        contact_person_mobile_no: customerData.contact_person_mobile_no || "",
        pan_number: customerData.pan_number || "",
        gstin: customerData.gstin || "",
        address: customerData.address || "",
        area: customerData.area || "",
        country: customerData.country || "",
        state: customerData.state || "",
        city: customerData.city || "",
        pincode: customerData.pincode || "",
      });

      const selectedCountry = Country.getAllCountries().find(
        (c) => c.name === customerData.country
      );
      if (selectedCountry) {
        const statesList = State.getStatesOfCountry(selectedCountry.isoCode);
        setStates(statesList);

        const selectedState = statesList.find(
          (s) => s.name === customerData.state
        );
        if (selectedState) {
          const citiesList = City.getCitiesOfState(
            selectedCountry.isoCode,
            selectedState.isoCode
          );
          setCities(citiesList);
        }
      }
    } else if (id) {
      Axios.get(`api/customers/${id}/`)
        .then((res) => {
          const data = res.data;
          setFormData({
            customer_name: data.customer_name || "",
            mobile_no: data.mobile_no || "",
            email: data.email || "",
            contact_person_name: data.contact_person_name || "",
            contact_person_mobile_no: data.contact_person_mobile_no || "",
            pan_number: data.pan_number || "",
            gstin: data.gstin || "",
            address: data.address || "",
            area: data.area || "",
            country: data.country || "",
            state: data.state || "",
            city: data.city || "",
            pincode: data.pincode || "",
          });

          const selectedCountry = Country.getAllCountries().find(
            (c) => c.name === data.country
          );
          if (selectedCountry) {
            const statesList = State.getStatesOfCountry(
              selectedCountry.isoCode
            );
            setStates(statesList);

            const selectedState = statesList.find((s) => s.name === data.state);
            if (selectedState) {
              const citiesList = City.getCitiesOfState(
                selectedCountry.isoCode,
                selectedState.isoCode
              );
              setCities(citiesList);
            }
          }
        })
        .catch(() => toast.error("Failed to load customer data"));
    }
  }, [id, customerData]);

  const handleCountryChange = (e: any, selectedCountry: any) => {
    setFormData((prev) => ({
      ...prev,
      country: selectedCountry?.name || "",
      state: "",
      city: "",
    }));
    if (selectedCountry) {
      setIsLoadingStates(true);
      const statesList = State.getStatesOfCountry(selectedCountry.isoCode);
      setStates(statesList);
      setCities([]);
      setIsLoadingStates(false);
    }
  };

  const handleStateChange = (e: any, selectedState: any) => {
    const selectedCountry = countries.find((c) => c.name === formData.country);
    setFormData((prev) => ({
      ...prev,
      state: selectedState?.name || "",
      city: "",
    }));
    if (selectedCountry && selectedState) {
      setIsLoadingCities(true);
      const citiesList = City.getCitiesOfState(
        selectedCountry.isoCode,
        selectedState.isoCode
      );
      setCities(citiesList);
      setIsLoadingCities(false);
    }
  };

  const handleCityChange = (e: any, selectedCity: any) => {
    setFormData((prev) => ({
      ...prev,
      city: selectedCity?.name || "",
    }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    let newValue = value;

    // For mobile number fields
    if (name === "mobile_no" || name === "contact_person_mobile_no") {
      newValue = value.replace(/\D/g, "").slice(0, 10);
    }

    // For pincode field
    if (name === "pincode") {
      newValue = value.replace(/\D/g, "").slice(0, 6);
    }

    // For PAN and GSTIN, auto uppercase
    if (name === "pan_number") newValue = value.toUpperCase().slice(0, 10);
    if (name === "gstin") newValue = value.toUpperCase().slice(0, 15);

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    // Clear error for this field as user types
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  // Enhanced validation function
  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.customer_name) {
      newErrors.customer_name = "Customer Name is required";
    } else if (!/^[A-Za-z\s]+$/.test(formData.customer_name)) {
      newErrors.customer_name = "Only alphabets and spaces are allowed";
    }

    // Enhanced mobile number validation
    if (!formData.mobile_no) {
      newErrors.mobile_no = "Mobile Number is required";
    } else if (!/^\d{10}$/.test(formData.mobile_no)) {
      newErrors.mobile_no = "Mobile Number must be exactly 10 digits";
    } else if (formData.mobile_no[0] === "0") {
      newErrors.mobile_no = "Mobile Number cannot start with 0";
    }

    if (!formData.email) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // if (!formData.contact_person_name) {
    //   newErrors.contact_person_name = "Contact Person Name is required";
    // } else if (!/^[A-Za-z\s]+$/.test(formData.contact_person_name)) {
    //   newErrors.contact_person_name = "Only alphabets and spaces are allowed";
    // }

    // // Enhanced contact person mobile validation
    // if (!formData.contact_person_mobile_no) {
    //   newErrors.contact_person_mobile_no =
    //     "Contact Person Mobile Number is required";
    // } else if (!/^\d{10}$/.test(formData.contact_person_mobile_no)) {
    //   newErrors.contact_person_mobile_no =
    //     "Mobile Number must be exactly 10 digits";
    // } else if (formData.contact_person_mobile_no[0] === "0") {
    //   newErrors.contact_person_mobile_no = "Mobile Number cannot start with 0";
    // }

    // if (!formData.pan_number) newErrors.pan_number = "PAN Number is required";
    // else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.pan_number)) {
    //   newErrors.pan_number =
    //     "Please enter a valid PAN number (e.g., ABCDE1234F)";
    // } else if (
    //   existingPANs.includes(formData.pan_number) &&
    //   (!id || formData.pan_number !== customerData?.pan_number)
    // ) {
    //   newErrors.pan_number = "This PAN Number is already used";
    // }

    // if (!formData.gstin) newErrors.gstin = "GSTIN is required";
    // else if (
    //   !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(
    //     formData.gstin
    //   )
    // ) {
    //   newErrors.gstin = "Please enter a valid GSTIN";
    // } else if (
    //   existingGSTINs.includes(formData.gstin) &&
    //   (!id || formData.gstin !== customerData?.gstin)
    // ) {
    //   newErrors.gstin = "This GSTIN is already used";
    // }

    // if (!formData.address) newErrors.address = "Address is required";
    // if (!formData.area) newErrors.area = "Area is required";
    // if (!formData.country) newErrors.country = "Country is required";
    // if (!formData.state) newErrors.state = "State is required";
    // if (!formData.city) newErrors.city = "City is required";

    // if (!formData.pincode) {
    //   newErrors.pincode = "Pincode is required";
    // } else if (!/^\d{6}$/.test(formData.pincode)) {
    //   newErrors.pincode = "Pincode must be exactly 6 digits";
    // }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setIsSubmitting(true);
      if (id) {
        await Axios.put(`api/customers/edit/${id}/`, formData);
        toast.success("Customer updated successfully");
      } else {
        await Axios.post("api/customers/create/", formData);
        toast.success("Customer added successfully");
      }
      setTimeout(() => {
        handleBack();
      }, 2000);
    } catch (error: any) {
      const backendErrors = error?.response?.data?.errors;
      const backendMessage = error?.response?.data?.message;

      const newErrors: Record<string, string> = {};

      if (backendErrors) {
        // If backendErrors is an object (field-wise)
        if (
          typeof backendErrors === "object" &&
          !Array.isArray(backendErrors)
        ) {
          Object.keys(backendErrors).forEach((key) => {
            newErrors[key] = Array.isArray(backendErrors[key])
              ? backendErrors[key].join(", ")
              : backendErrors[key];
          });
        }
        // Don't assign string errors to email field - let toast handle general errors
      }

      setErrors((prev) => ({
        ...prev,
        ...newErrors,
      }));

      const firstError =
        Object.values(newErrors)[0] ||
        backendMessage ||
        "Failed to save customer";

      toast.error(firstError);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-content px-4">
      {/* <ToastContainer/> */}
      <div className="p-4 bg-white rounded shadow-sm">
        <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
          <div className="d-flex align-items-center">
            <span
              className="me-2"
              style={{ fontSize: "1.25rem", cursor: "pointer" }}
              onClick={handleBack}
            >
              <BsArrowLeft />
            </span>
            <h5 className="mb-0 fw-bold">
              {id ? "Edit" : "Add"} Customer/Company
            </h5>
          </div>
          <div className="text-muted" style={{ fontSize: "0.9rem" }}>
            <span
              className="text-muted"
              style={{ fontSize: "0.9rem", cursor: "pointer" }}
              onClick={handleBack}
            >
              Customer/Company
            </span>{" "}
            &gt;{" "}
            <span className="text-muted" style={{ fontSize: "0.9rem", cursor: "pointer" }}>
              {id ? "Edit Customer/Company" : "Add Customer/Company"}
            </span>
          </div>
        </div>
        <Form autoComplete="off" onSubmit={handleSubmit}>
          {/*  Customer Details Section */}
          <div className="mb-4">
            <h6 className="fw-bold border-bottom pb-2">
              Customer/Company Details
            </h6>
            <Row className="g-3 mt-2">
              <Col lg={3}>
                <Form.Label>
                  Customer / Company Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="customer_name"
                  placeholder="Enter customer or company name"
                  value={formData.customer_name}
                  onChange={(e: any) => {
                    const value = e.target.value;
                    // Allow letters, numbers, spaces, dots, commas, hyphens, and ampersands
                    if (/^[A-Za-z0-9\s.,&-]*$/.test(value)) {
                      handleChange(e);
                    }
                  }}
                  isInvalid={!!errors.customer_name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.customer_name}
                </Form.Control.Feedback>
              </Col>

              <Col lg={3}>
                <Form.Label>
                  Mobile Number <span className="text-danger">*</span>
                </Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <BsTelephone /> +91
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    name="mobile_no"
                    placeholder="Enter Mobile Number"
                    value={formData.mobile_no}
                    onChange={handleChange}
                    isInvalid={!!errors.mobile_no}
                    inputMode="numeric"
                    pattern="[0-9]*"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.mobile_no}
                  </Form.Control.Feedback>
                </InputGroup>
              </Col>
              <Col lg={3}>
                <Form.Label>
                  Email <span className="text-danger">*</span>
                </Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <BsEnvelope />
                  </InputGroup.Text>
                  <Form.Control
                    type="email"
                    name="email"
                    placeholder="Enter Email"
                    value={formData.email}
                    onChange={handleChange}
                    isInvalid={!!errors.email}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.email}
                  </Form.Control.Feedback>
                </InputGroup>
              </Col>
              <Col lg={3}>
                <Form.Label>Contact Person Name</Form.Label>
                <Form.Control
                  type="text"
                  name="contact_person_name"
                  placeholder="Enter Contact Person Name"
                  value={formData.contact_person_name}
                  onChange={(e: any) => {
                    const value = e.target.value;
                    if (/^[A-Za-z\s]*$/.test(value)) {
                      handleChange(e);
                    }
                  }}
                  isInvalid={!!errors.contact_person_name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.contact_person_name}
                </Form.Control.Feedback>
              </Col>
              <Col lg={3}>
                <Form.Label>Contact Person Mobile Number </Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <BsTelephone /> +91
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    name="contact_person_mobile_no"
                    placeholder="Enter Contact Person Mobile Number"
                    value={formData.contact_person_mobile_no}
                    onChange={handleChange}
                    isInvalid={!!errors.contact_person_mobile_no}
                    inputMode="numeric"
                    pattern="[0-9]*"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.contact_person_mobile_no}
                  </Form.Control.Feedback>
                </InputGroup>
              </Col>
              <Col lg={3}>
                <Form.Label>PAN Number</Form.Label>
                <Form.Control
                  type="text"
                  name="pan_number"
                  placeholder="Enter Pan Number"
                  value={formData.pan_number}
                  onChange={(e: any) => {
                    // Convert to uppercase and limit to 10 characters
                    const value = e.target.value.toUpperCase().slice(0, 10);
                    e.target.value = value;
                    handleChange(e);
                  }}
                  isInvalid={!!errors.pan_number}
                  maxLength={10}
                  // style={{ textTransform: 'uppercase' }}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.pan_number}
                </Form.Control.Feedback>
              </Col>
              <Col lg={3}>
                <Form.Label>GSTIN</Form.Label>
                <Form.Control
                  type="text"
                  name="gstin"
                  placeholder="Enter GSTIN"
                  value={formData.gstin}
                  onChange={(e: any) => {
                    // Convert to uppercase and limit to 15 characters
                    const value = e.target.value.toUpperCase().slice(0, 15);
                    e.target.value = value;
                    handleChange(e);
                  }}
                  isInvalid={!!errors.gstin}
                  maxLength={15}
                  // style={{ textTransform: 'uppercase' }}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.gstin}
                </Form.Control.Feedback>
              </Col>
            </Row>
          </div>

          {/*  Address Details Section */}
          <div className="mb-4">
            <h6 className="fw-bold border-bottom pb-2">Address Details</h6>
            <Row className="g-3 mt-2">
              <Col lg={6}>
                <Form.Label>Address</Form.Label>
                <Form.Control
                  type="text"
                  name="address"
                  placeholder="Enter Address"
                  value={formData.address}
                  onChange={handleChange}
                  isInvalid={!!errors.address}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.address}
                </Form.Control.Feedback>
              </Col>
              <Col lg={6}>
                <Form.Label>Area</Form.Label>
                <Form.Control
                  type="text"
                  name="area"
                  placeholder="Enter Area"
                  value={formData.area}
                  onChange={handleChange}
                  isInvalid={!!errors.area}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.area}
                </Form.Control.Feedback>
              </Col>
              {/* Country */}
              <Col md={4}>
                <Form.Label>Country</Form.Label>
                <Autocomplete
                  disablePortal
                  options={countries}
                  getOptionLabel={(option) => option.name}
                  value={
                    countries.find((c) => c.name === formData.country) || null
                  }
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  renderOption={(props, option) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (option) => option.name
                    )
                  }
                  onChange={(e, selectedCountry) => {
                    setFormData((prev) => ({
                      ...prev,
                      country: selectedCountry?.name || "",
                      state: "",
                      city: "",
                    }));
                    // Clear validation error immediately
                    setErrors((prev) => ({ ...prev, country: "" }));

                    if (selectedCountry) {
                      setIsLoadingStates(true);
                      const statesList = State.getStatesOfCountry(
                        selectedCountry.isoCode
                      );
                      setStates(statesList);
                      setCities([]);
                      setIsLoadingStates(false);
                    }
                  }}
                  renderInput={(params) => (
                    <BootstrapTextField
                      {...params}
                      placeholder="Select Country"
                      size="small"
                      error={!!errors.country}
                      helperText={errors.country}
                      fullWidth
                      sx={autocompleteDropdownStyles.inputSx}
                    />
                  )}
                />
              </Col>

              {/* State */}
              <Col md={4}>
                <Form.Label>State</Form.Label>
                <Autocomplete
                  disablePortal
                  options={states}
                  getOptionLabel={(option) => option.name}
                  loading={isLoadingStates}
                  value={states.find((s) => s.name === formData.state) || null}
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  renderOption={(props, option) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (option) => option.name
                    )
                  }
                  onChange={(e, selectedState) => {
                    setFormData((prev) => ({
                      ...prev,
                      state: selectedState?.name || "",
                      city: "",
                    }));
                    // Clear validation error immediately
                    setErrors((prev) => ({ ...prev, state: "" }));

                    const selectedCountry = countries.find(
                      (c) => c.name === formData.country
                    );
                    if (selectedCountry && selectedState) {
                      setIsLoadingCities(true);
                      const citiesList = City.getCitiesOfState(
                        selectedCountry.isoCode,
                        selectedState.isoCode
                      );
                      setCities(citiesList);
                      setIsLoadingCities(false);
                    }
                  }}
                  renderInput={(params) => (
                    <BootstrapTextField
                      {...params}
                      placeholder="Select State"
                      size="small"
                      error={!!errors.state}
                      helperText={errors.state}
                      fullWidth
                      sx={autocompleteDropdownStyles.inputSx}
                    />
                  )}
                  disabled={!formData.country}
                />
              </Col>

              {/* City */}

              {/* City */}
              <Col md={4}>
                <Form.Label>City</Form.Label>
                <Autocomplete
                  disablePortal
                  options={cities}
                  getOptionLabel={(option) => option.name}
                  loading={isLoadingCities}
                  value={cities.find((c) => c.name === formData.city) || null}
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  renderOption={(props, option) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (option) => option.name
                    )
                  }
                  onChange={(e, selectedCity) => {
                    setFormData((prev) => ({
                      ...prev,
                      city: selectedCity?.name || "",
                    }));
                    // Clear validation error immediately
                    setErrors((prev) => ({ ...prev, city: "" }));
                  }}
                  renderInput={(params) => (
                    <BootstrapTextField
                      {...params}
                      placeholder="Select City"
                      size="small"
                      error={!!errors.city}
                      helperText={errors.city}
                      fullWidth
                      sx={autocompleteDropdownStyles.inputSx}
                    />
                  )}
                  disabled={!formData.state}
                />
              </Col>

              <Col md={4}>
                <Form.Label>Pincode</Form.Label>
                <Form.Control
                  type="text"
                  name="pincode"
                  placeholder="Enter Pincode"
                  value={formData.pincode}
                  onChange={handleChange}
                  isInvalid={!!errors.pincode}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.pincode}
                </Form.Control.Feedback>
              </Col>
            </Row>
          </div>

          {/*Actions */}
          <div className="d-flex justify-content-center gap-3 py-3">
            <Button
              variant="outline-secondary"
              style={{
                color: "#878899",
                borderColor: "#B0BAC9",
                backgroundColor: "transparent",
              }}
              onClick={handleBack}
            >
              Cancel
            </Button>

            {/* Reusable submit button with spinner */}
            <SubmitButton isSubmitting={isSubmitting} isEdit={!!id} />
          </div>
        </Form>
      </div>
    </div>
  );
};

export default AddCustomerForm;
